import { Flex, Td, Text, Tr } from '@chakra-ui/react';
import { getIn } from 'formik';
import React from 'react';

import {
  error as commonError,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  typeguards as finleyTypeguard,
  Util,
} from '@finley-technologies/finley-types';

import { ReportingGlobalRecurringDateConfigs } from '../../property/globalRecurringDate/ReportingGlobalRecurringDateConfigs';
import { RecurringDateConfigView } from '../../property/RecurringDateConfigView';
import { PostgresQueriesView } from '../covenantPostgresQuery/PostgresQueryView';
import { addFinancialCovenantConfig } from './AddFinancialCovenantModal';
import { FinancialCovenantEditCovenantValueContainer } from './FinancialCovenantEditCovenantValueContainer';
import { FinancialCovenantLimitValueTypeSelector } from './FinancialCovenantValueType';
import { AthenaQueriesView } from 'packages/athena/ui/AthenaQueryView';
import {
  AddIconButton,
  DeleteIconButton,
  IconButton,
  SaveIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { formatSelectOptions } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AthenaRecurringQueriesContainer } from '~/packages/athena/ui/AthenaRecurringQueryView';
import { useSendFinancialCovenantWorklease } from '~/packages/covenants/hooks';

const deleteFinancialCovenantConfig = (
  wc: WipConfig,
  fullPropertyKey: string,
): void => {
  const covenant = getIn(wc.formProps.values, fullPropertyKey);
  finleyAssert(
    finleyTypeguard.creditAgreement.isFinancialCovenantProperty(covenant),
    new commonError.FinleyError(
      'Expected "Financial Covenant Property" structure',
      { metadata: { logMeta: { covenant } } },
    ),
  );

  const { _config_: _unused, ...remainingCovenant } = covenant;
  wc.formProps.setFieldValue(fullPropertyKey, remainingCovenant);
};

type FinancialCovenantConfigProps = {
  organizationId: string;
  limit: CreditAgreement.DomainEntity.FinancialCovenantLimit;
  _config_?: CreditAgreement.Config.FinancialCovenant.Config;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  headersLength: number;
  triggerDate: Util.FullDateStr;
  covenantKey: string;
};
export const FinancialCovenantConfig = ({
  organizationId,
  limit,
  _config_,
  wipConfig,
  fullPropertyKey,
  headersLength,
  triggerDate,
  covenantKey,
}: FinancialCovenantConfigProps) => {
  const {
    sendCreditAgreementFinancialCovenantWorklease,
    isSendFinCovWorkleaseLoading,
    evaluateCreditAgreementDevFinCovWorklease,
    isEvaluateDevFinCovWorkleaseLoading,
  } = useSendFinancialCovenantWorklease();

  if (!_config_) {
    return wipConfig?.isMutateAllowed(fullPropertyKey) ? (
      <Tr>
        <Td colSpan={headersLength}>
          <Flex alignItems="center">
            <Text mr="2">Add Configuration</Text>
            <AddIconButton
              ariaLabel="add config"
              label="Add Configuration"
              onClick={() => {
                addFinancialCovenantConfig(wipConfig, fullPropertyKey);
              }}
            />
          </Flex>
        </Td>
      </Tr>
    ) : (
      <Tr>
        <Td>
          <Text>No Configuration Exists</Text>
        </Td>
      </Tr>
    );
  }

  const presetVariables = [
    { name: 'triggerDate', value: triggerDate, parseNumber: true },
  ];

  return (
    <Tr>
      <Td colSpan={headersLength}>
        <Flex flexDir="column">
          <Flex alignItems="center" mb="3">
            <Text fontSize="large" fontWeight="bold" mr="2">
              Config
            </Text>
            {wipConfig?.isMutateAllowed(fullPropertyKey) && (
              <>
                <DeleteIconButton
                  ariaLabel="delete config"
                  label="Delete Configuration"
                  onClick={() => {
                    deleteFinancialCovenantConfig(wipConfig, fullPropertyKey);
                  }}
                />
                <SaveIconButton
                  ariaLabel="save config"
                  label="Save Configuration"
                  onClick={() => {
                    wipConfig.saveCreditAgreementFullArtifactWip({
                      variables: {
                        creditAgreementFullArtifactWip: {
                          id: wipConfig.formProps.values.id,
                          creditFacilityId:
                            wipConfig.formProps.values.creditFacilityId,
                          creditAgreement: JSON.stringify(
                            wipConfig.formProps.values,
                          ),
                        },
                      },
                    });
                  }}
                  isLoading={
                    wipConfig.isSaveCreditAgreementFullArtifactWipLoading
                  }
                />

                <UploadIconButton
                  ariaLabel="qa financial covenant"
                  label="QA Financial Covenant"
                  onClick={() => {
                    sendCreditAgreementFinancialCovenantWorklease({
                      wipConfig,
                      covenantKey,
                      triggerDate,
                    });
                  }}
                  isLoading={isSendFinCovWorkleaseLoading}
                />

                {/*
                 * Only exposing this QA tool on Dev since don't want to lock
                 * up FinleyCore when evaluating port covs
                 */}
                {commonUtils.EnvUtils.isDevelopment() && (
                  <IconButton
                    ariaLabel="(dev) qa financial covenant"
                    label="(Dev) QA Financial Covenant"
                    isLoading={isEvaluateDevFinCovWorkleaseLoading}
                    onClick={() => {
                      evaluateCreditAgreementDevFinCovWorklease({
                        wipConfig,
                        covenantKey,
                        triggerDate,
                      });
                    }}
                    icon={<Icon icon="dev" type="brand" />}
                  />
                )}
              </>
            )}
          </Flex>

          {/* *** Limit Value Type *** */}
          <FinancialCovenantLimitValueTypeSelector
            wipConfig={wipConfig}
            fullPropertyKey={`${fullPropertyKey}.limit`}
            limitProperty={limit}
          />

          {/* *** Trigger Date *** */}
          <RecurringDateConfigView
            recurringDateConfig={_config_.triggerDates}
            wipConfig={wipConfig}
            fullPropertyKey={`${fullPropertyKey}._config_.triggerDates`}
            displayName="Trigger Dates"
            enableEdit
          />

          {/* *** Recurring Date Configs *** */}
          <ReportingGlobalRecurringDateConfigs
            wipConfig={wipConfig}
            globalRecurringDateConfigs={_config_.globalRecurringDateConfigs}
            basePropertyKey={`${fullPropertyKey}._config_.globalRecurringDateConfigs`}
            source="finCovs"
          />

          {/* *** Athena Query *** */}
          <AthenaQueriesView
            organizationId={organizationId}
            athenaQueries={_config_.athenaQueries}
            wipConfig={wipConfig}
            basePropertyKey={`${fullPropertyKey}._config_.athenaQueries`}
            inputDateType="triggerDate"
            presetVariables={presetVariables}
            variableSubTitle={
              '"triggerDate" will be an available variable by default that can be used'
            }
          />

          {/* *** Athena Recurring Query *** */}
          <AthenaRecurringQueriesContainer
            organizationId={organizationId}
            wipConfig={wipConfig}
            athenaRecurringQueries={_config_.athenaRecurringQueries}
            globalRecurringDateConfigs={_config_.globalRecurringDateConfigs}
            basePropertyKey={`${fullPropertyKey}._config_.athenaRecurringQueries`}
            inputDateType="triggerDate"
            presetVariables={presetVariables}
            variableSubTitle={
              '"triggerDate" will be an available variable by default that can be used'
            }
          />

          {/* *** Postgres Query *** */}
          <PostgresQueriesView
            wipConfig={wipConfig}
            triggerDate={triggerDate}
            postgresQueries={_config_.postgresQueries}
            basePropertyKey={`${fullPropertyKey}._config_.postgresQueries`}
            inputDateType="triggerDate"
            presetVariables={presetVariables}
            selectOptions={formatSelectOptions(
              CreditAgreement.Config.FinancialCovenant.PostgresQuery
                .QueryTypeIterable,
              'title',
            )}
          />

          {/* *** Intermediate Fields *** */}
          <FinancialCovenantEditCovenantValueContainer
            wipConfig={wipConfig}
            fullPropertyKey={fullPropertyKey}
            triggerDate={triggerDate}
            intermediateFields={_config_.intermediateFields}
          />
        </Flex>
      </Td>
    </Tr>
  );
};
